import React, {
  useEffect, useMemo, useState, useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { PlusOutlined } from '@ant-design/icons';
import { DragDropContext } from 'react-beautiful-dnd';

import BreadcrumbContainer from '../common/breadcrumbContainer/breadcrumbContainer';
import OnTraccrEmpty from '../common/OnTraccrEmpty';

import BoardHeader from './BoardHeader';
import BoardAddDrawer from './BoardAddDrawer';
import BoardView from './BoardCardView/BoardView';
import BoardListView from './BoardListView/BoardListView';
import BoardCardDrawer from './BoardCardDrawer';
import LargeBoard from './LargeBoard/LargeBoard';

import Permissions from '../auth/Permissions';

import {
  getBoards,
  getBoardCardTemplates,
  getBoardDetails,
  openCardDrawer,
  closeFormDrawer,
  getFormMappings,
  getBoardTemplates,
  getShortLists,
  moveCard,
  setCardDragging,
  getBoardFilterViews,
} from './state/boards.actions';
import {
  getUserLabels,
  getUsers,
  getUserCustomFieldTemplate,
} from '../users/state/users.actions';
import { getMaterials } from '../materials/state/materials.actions';
import {
  getCustomerLabels,
  getCustomers,
  getCustomerCustomFieldTemplate,
} from '../contacts/customers/state/customers.actions';
import { getEquipment } from '../equipment/state/equipment.actions';
import {
  getClientPortalAccounts,
  getClientPortalSettings,
} from '../clientPortal/state/clientPortal.actions';
import { getTemplates, getCustomTables, getAssignedFormTemplates } from '../forms/state/forms.actions';
import { getSavedAnalyticsReports } from '../analytics/state/analytics.actions';
import { getSavedDashboards } from '../dashboard/state/dashboard.actions';
import { getCustomFields } from '../timecards/state/timecards.actions';
import {
  getProjectCustomFieldTemplate,
  getProjectScheduleOfValues,
} from '../projects/state/projects.actions';

import { getCostcodeCustomFieldTemplate } from '../costcodes/state/costcodes.actions';
import { getBuckets } from '../buckets/state/buckets.actions';
import { getScheduleCategories } from '../schedule/state/schedule.actions';

import BoardSelector from './BoardSelector';

import FormResponder from '../forms/FormResponder';
import { handleCardMove } from './boards.helpers';

import { BOARD_CARD_ANALYTICS_TYPE } from '../analytics/analytics.constants';
import BoardTimelineView from './BoardTimelineView/BoardTimelineView';
import { VIEW_TYPES } from './boards.constants';
import useToggle from '../common/hooks/useToggle';
import useBoardPermissions from '../common/hooks/useBoardPermissions';
import useFilteredBoardList from '../common/hooks/useFilteredBoardList';
import CustomFieldFormMappingDrawer from '../forms/CustomFieldFormMappingDrawer/CustomFieldFormMappingDrawer';
import ShortListCardDrawer from './ShortLists/ShortListCardDrawer';
import { getStatuses } from '../statuses/state/statuses.actions';

const CRUMBS = [{
  text: 'Boards',
  icon: 'group',
}];

const STORAGE_KEY = 'selectedBoardId';
export default function Boards() {
  const dispatch = useDispatch();
  const {
    pathname,
    state: {
      boardId: locationBoardId,
      cardId: locationCardId,
    } = {},
  } = useLocation();
  const history = useHistory();

  const boards = useSelector((state) => state.boards.boards);
  const selectedDivisions = useSelector((state) => state.settings.selectedDivisions);
  const boardTags = useSelector((state) => state.boards.myCardTags);
  const viewType = useSelector((state) => state.boards.viewType);
  const menuCollapsed = useSelector((state) => state.main.menuCollapsed);
  const latestManualDraftId = useSelector((state) => state.forms.latestManualDraftId);
  const {
    visible: formDrawerVisible = false,
    assignedForm,
    formData,
    cardId: formCardId,
    isEdit: selectedFormIsEdit,
    fileMap,
    ids: preloadedIds,
  } = useSelector((state) => state.boards.formDrawerConfig);
  const {
    visible: formMapDrawerVisible = false,
    selectedCardId: formMapCardId,
  } = useSelector((state) => state.boards.formMapDrawerConfig);
  const {
    users = [],
  } = useSelector((state) => state.boards.selectedBoard || {});
  const numCards = useSelector((state) => state.boards.cardIds?.length ?? 0);

  const [addDrawerVisible, setAddDrawerVisible] = useState(false);
  const [selectedBoardId, setSelectedBoardId] = useState();
  const [cardSearchBoard, setCardSearchBoard] = useState(null);

  const {
    isToggled: loading,
    toggle: toggleLoading,
  } = useToggle();

  // Drag and drop state
  const statusToCardIdMap = useSelector((state) => state.boards.statusToCardIdMap);
  const fullCards = useSelector((state) => state.boards.fullCards);

  const fullSelectedBoard = useMemo(
    () => boards[selectedBoardId] || {},
    [boards, selectedBoardId],
  );

  const assignedDraft = useMemo(() => {
    const relevantDraftId = latestManualDraftId ?? assignedForm?.draftId;
    if (!assignedForm) return { draftId: relevantDraftId };
    return {
      ...assignedForm,
      draftId: relevantDraftId,
    };
  }, [assignedForm, latestManualDraftId]);

  const {
    creatorId,
  } = fullSelectedBoard;

  const {
    [fullSelectedBoard.cardTypeId]:
    cardType,
  } = useSelector((state) => state.boards.cardTemplates);

  const largeBoardMode = numCards > 2500;

  const userBoardPermissions = useBoardPermissions({ users, creatorId });

  const { canCreate } = userBoardPermissions;

  const showAddDrawer = useCallback(() => setAddDrawerVisible(true), []);
  const hideAddDrawer = useCallback(() => setAddDrawerVisible(false), []);
  const hideFormDrawer = useCallback(() => dispatch(closeFormDrawer()), [dispatch]);

  // Drag and drop handlers
  const onDragStart = useCallback((e) => {
    dispatch(setCardDragging(true, parseInt(e.source.droppableId, 10)));
  }, [dispatch]);

  const onDragEnd = useCallback((dragEvent) => {
    dispatch(setCardDragging(false));

    const {
      draggableId,
      destinationId,
      destinationIndex,
      sourceId,
      sourceIndex,
    } = handleCardMove({
      disableCrossColumnSorting: fullSelectedBoard.disableCrossColumnSorting,
      cardsByStatusId: statusToCardIdMap,
      dragEvent,
      fullCards,
    });
    const { color: originalColor } = fullCards[draggableId] || {};
    if (!draggableId) return;
    dispatch(moveCard(draggableId, {
      boardId: selectedBoardId,
      destinationId,
      destinationIndex,
      sourceId,
      sourceIndex,
      color: originalColor,
    }));
  }, [
    dispatch,
    selectedBoardId,
    fullSelectedBoard.disableCrossColumnSorting,
    statusToCardIdMap,
    fullCards,
  ]);

  useEffect(() => {
    dispatch(getBoards());
    dispatch(getBoardTemplates());
    dispatch(getBoardCardTemplates());
    dispatch(getUserLabels());
    dispatch(getUsers());
    dispatch(getCustomers());
    dispatch(getCustomerLabels());
    dispatch(getMaterials());
    dispatch(getEquipment());
    dispatch(getClientPortalAccounts());
    dispatch(getClientPortalSettings());
    dispatch(getTemplates());
    dispatch(getCustomTables());
    if (Permissions.has('MANAGE_ANALYTICS')) {
      dispatch(getSavedAnalyticsReports(BOARD_CARD_ANALYTICS_TYPE));
    }
    dispatch(getSavedDashboards(BOARD_CARD_ANALYTICS_TYPE));
    dispatch(getAssignedFormTemplates());
    dispatch(getCustomFields());
    dispatch(getProjectCustomFieldTemplate());
    dispatch(getShortLists());
    dispatch(getCostcodeCustomFieldTemplate());
    dispatch(getProjectScheduleOfValues({}));
    dispatch(getUserCustomFieldTemplate());
    dispatch(getBuckets());
    dispatch(getScheduleCategories());
    dispatch(getCustomerCustomFieldTemplate());
    dispatch(getStatuses());
  }, []);

  useEffect(() => {
    const loadData = async () => {
      toggleLoading();
      await Promise.all([
        dispatch(getBoardDetails(selectedBoardId)),
        dispatch(getFormMappings(selectedBoardId)),
      ]);
      await dispatch(getBoardFilterViews(selectedBoardId));
      toggleLoading();
    };
    if (selectedBoardId) {
      loadData();
    }
  }, [selectedBoardId]);

  const tagsPerBoard = useMemo(() => {
    const tpbMap = {};
    boardTags.forEach((tag) => {
      const { boardId } = tag;
      const {
        [boardId]: oldCount = 0,
      } = tpbMap;
      tpbMap[boardId] = oldCount + 1;
    });
    return tpbMap;
  }, [boardTags]);
  const boardList = useFilteredBoardList();
  const noBoards = boardList.length === 0;
  useEffect(() => {
    const storageBoardId = window.localStorage.getItem(STORAGE_KEY);
    if (
      selectedBoardId == null
      && storageBoardId
      && storageBoardId in boards
    ) {
      setSelectedBoardId(storageBoardId);
      return;
    }
    let nextBoardId;

    if (fullSelectedBoard && fullSelectedBoard.id) {
      if (
        fullSelectedBoard?.divisions?.some?.((divisionId) => (
          selectedDivisions.has(divisionId)
        ))
      ) {
        nextBoardId = fullSelectedBoard.id;
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
        nextBoardId = boardList[0]?.id;
      }
      // Initial Load
    } else if (locationBoardId && locationBoardId in boards) {
      nextBoardId = locationBoardId;
    } else if (storageBoardId && storageBoardId in boards) {
      nextBoardId = storageBoardId;
    } else if (boardList.length > 0) {
      nextBoardId = boardList[0].id;
    }

    if (nextBoardId) {
      if (window.localStorage.getItem(STORAGE_KEY) !== nextBoardId) {
        window.localStorage.setItem(STORAGE_KEY, nextBoardId);
      }
      setSelectedBoardId(nextBoardId);
    }
  }, [fullSelectedBoard, selectedDivisions, boardList, boards, locationBoardId, selectedBoardId]);

  useEffect(() => {
    if (dispatch && locationCardId) {
      setTimeout(() => {
        dispatch(openCardDrawer({ selectedCardId: locationCardId }));
        history.replace(pathname);
      }, 250);
    }
  }, [dispatch, locationCardId, pathname, history]);

  const onBoardSelected = useCallback((boardId) => {
    setSelectedBoardId(boardId);
  }, []);

  const onCardDrawerClose = useCallback(() => {
    setCardSearchBoard(null);
  }, []);

  const onCardSearchView = useCallback((card) => {
    setCardSearchBoard(card?.boardId ?? null);
  }, []);

  const fullCrumbs = useMemo(() => {
    if (boardList.length === 0 || !fullSelectedBoard) {
      if (!Permissions.has('BOARDS_WRITE')) return CRUMBS;
      return [{
        text: (
          <span>
            Boards
            <span className="analytics-title-button" style={{ transform: 'translateY(-10px)' }}>
              <PlusOutlined onClick={showAddDrawer} style={{ fontSize: 20 }} />
            </span>
          </span>
        ),
        icon: 'group',
      }];
    }
    return CRUMBS.concat([{
      text: (
        <BoardSelector
          fullSelectedBoard={fullSelectedBoard}
          boardList={boardList}
          largeBoardMode={largeBoardMode}
          tagsPerBoard={tagsPerBoard}
          onBoardSelected={onBoardSelected}
          onAdd={showAddDrawer}
          loading={loading}
        />
      ),
    }]);
  }, [
    boardList,
    fullSelectedBoard,
    selectedDivisions,
    tagsPerBoard,
    largeBoardMode,
    onBoardSelected,
    loading,
  ]);

  const body = useMemo(() => {
    if (noBoards || !fullSelectedBoard) return <OnTraccrEmpty title="Boards" onAddNewClick={showAddDrawer} />;
    if (largeBoardMode) return <LargeBoard />;
    // eslint-disable-next-line react/jsx-props-no-spreading
    switch (viewType) {
      case VIEW_TYPES.LIST_VIEW: {
        return (
          <BoardListView
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...fullSelectedBoard}
            useCardNumberAsCardTitle={cardType?.useCardNumberAsCardTitle}
            userBoardPermissions={userBoardPermissions}
            loading={loading}
            onBoardSelected={onBoardSelected}
          />
        );
      }
      case VIEW_TYPES.TIMELINE_VIEW: {
        return (
          <BoardTimelineView
            id={fullSelectedBoard?.id}
            cardTypeId={fullSelectedBoard?.cardTypeId}
            isLocked={fullSelectedBoard?.isLocked}
            disableCrossColumnSorting={fullSelectedBoard?.disableCrossColumnSorting}
            showCardNumber={fullSelectedBoard?.showCardNumber}
            useCardNumberAsCardTitle={cardType?.useCardNumberAsCardTitle}
            userBoardPermissions={userBoardPermissions}
            timelineDisplayFields={fullSelectedBoard?.timelineDisplayFields}
            onBoardSelected={onBoardSelected}
          />
        );
      }
      default: {
        return (
          <BoardView
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...fullSelectedBoard}
            useCardNumberAsCardTitle={cardType?.useCardNumberAsCardTitle}
            userBoardPermissions={userBoardPermissions}
            onBoardSelected={onBoardSelected}
            loading={loading}
          />
        );
      }
    }
  }, [
    noBoards,
    largeBoardMode,
    fullSelectedBoard,
    viewType,
    userBoardPermissions,
    loading,
    onBoardSelected,
  ]);

  return (
    <>
      <BreadcrumbContainer crumbs={fullCrumbs} fullscreen={menuCollapsed}>
        <BoardHeader
          selectedBoard={fullSelectedBoard}
          useCardNumberAsCardTitle={cardType?.useCardNumberAsCardTitle}
          onBoardSelect={onBoardSelected}
          largeBoardMode={largeBoardMode}
          canCreate={canCreate}
          onCardSearchView={onCardSearchView}
          loading={loading}
        />
        <DragDropContext
          onDragEnd={onDragEnd}
          onDragStart={onDragStart}
        >
          {body}
        </DragDropContext>
      </BreadcrumbContainer>
      <BoardAddDrawer
        visible={addDrawerVisible}
        onClose={hideAddDrawer}
      />
      <BoardCardDrawer
        boardId={cardSearchBoard ?? selectedBoardId}
        cardTypeId={fullSelectedBoard.cardTypeId}
        divisions={fullSelectedBoard.divisions}
        showCardNumber={fullSelectedBoard.showCardNumber}
        useCardNumberAsCardTitle={cardType?.useCardNumberAsCardTitle}
        userBoardPermissions={userBoardPermissions}
        onBoardSelected={onBoardSelected}
        onClose={onCardDrawerClose}
      />
      <CustomFieldFormMappingDrawer
        visible={formMapDrawerVisible}
        selectedProfileId={formMapCardId}
        profileTypeId={fullSelectedBoard.cardTypeId}
        divisions={fullSelectedBoard.divisions}
        type="card"
      />
      <FormResponder
        visible={formDrawerVisible}
        onClose={hideFormDrawer}
        assignedForm={assignedDraft}
        preloadedData={formData}
        preloadedIds={preloadedIds}
        isOnEditStep={selectedFormIsEdit}
        cardId={formCardId}
        initialFileMap={fileMap}
      />
      <ShortListCardDrawer />
    </>
  );
}
